import { useId, useState } from "react";

import { Button, cn, Icon, SortableList, TextField } from "@go-novus/ui";

import * as m from "#/paraglide/messages";
import { moveItem } from "#/shared/lib/move-item";

const MAX_QUESTIONS = 3;

function createRowKeys(count: number): string[] {
  return Array.from({ length: count }, () => crypto.randomUUID());
}

interface QuestionRow {
  id: string;
  question: string;
}

export interface SuggestedQuestionsFieldProps {
  /** 按行号给的错误，空行和重复都报在对应那一行。 */
  errors: Record<number, string> | undefined;
  onChange: (next: string[]) => void;
  questions: string[];
}

/** 空行别在保存前偷偷丢掉：留给校验拦下，商家自己填上或删掉。 */
export function SuggestedQuestionsField({
  errors,
  onChange,
  questions,
}: SuggestedQuestionsFieldProps) {
  const baseId = useId();
  // 问题只是字符串，没有 id 可当 key：行的身份记在这里，增删和移动时跟问题一起变。
  const [rowKeys, setRowKeys] = useState(() => createRowKeys(questions.length));
  // 表单被外面整份换掉（比如读回别处保存的配置）、行数对不上时，旧身份作废，整份重发。
  if (rowKeys.length !== questions.length) {
    setRowKeys(createRowKeys(questions.length));
  }
  const full = questions.length >= MAX_QUESTIONS;
  // 点「添加」后光标得落进新行：停在按钮上时，商家接着打字，空格会一直按这个按钮加空行。
  const [focusRowKey, setFocusRowKey] = useState<string | null>(null);

  // 行数对不上是被丢弃的那一帧（上面已触发整份重发，不会提交），位置兜底只为让 id 有值。
  const rows = questions.map((question, index): QuestionRow => ({
    id: rowKeys[index] ?? `pending-${index}`,
    question,
  }));

  // 在 ref 回调里聚焦，别改成 effect 里按 id 找：TextField 的 id 要晚一步才写到输入框上，那时找不到。
  function focusNewRow(node: HTMLDivElement | null): void {
    if (node === null) return;
    node.querySelector("input")?.focus();
    setFocusRowKey(null);
  }

  /** 增、删、挪都先算出一份新 rows，再把行身份和问题一起换掉，两边不错位。 */
  function applyRows(nextRows: QuestionRow[]): void {
    setRowKeys(nextRows.map((row) => row.id));
    onChange(nextRows.map((row) => row.question));
  }

  function add(): void {
    const rowKey = crypto.randomUUID();
    setFocusRowKey(rowKey);
    applyRows([...rows, { id: rowKey, question: "" }]);
  }

  function move(rowId: string, toIndex: number): void {
    applyRows(moveItem(rows, rowId, toIndex));
  }

  function remove(index: number): void {
    applyRows(rows.filter((_, itemIndex) => itemIndex !== index));
  }

  return (
    <div className="border-t border-[var(--border-surface-default)] p-5">
      <div className="text-label font-semibold">
        {m.settings_suggested_questions_label()}
      </div>
      <p className="m-0 mt-1 text-micro [line-height:1.45] text-[var(--content-fg-subtle)]">
        {m.settings_suggested_questions_hint()}
      </p>
      {rows.length > 0 && (
        <div className="mt-3">
          <SortableList
            as="ol"
            items={rows}
            onMove={move}
            renderItem={(row, { handle, index }) => {
              const position = index + 1;
              const id = `${baseId}-${row.id}`;

              return (
                <div
                  className={cn(
                    "relative flex items-start gap-2",
                    index > 0 && "mt-2",
                  )}
                >
                  {/* 把手钉在输入框高度上：行下面可能有报错文字，
                      self-stretch 的把手不能被它撑高。 */}
                  <div className="flex min-h-control flex-none items-center">
                    {handle}
                  </div>
                  {/* TextField 收不了 aria-label，名字靠这个 label 的 for 挂上去。
                      relative 兜住 sr-only 的绝对定位，免得撑出整页滚动条。 */}
                  <label className="sr-only" htmlFor={id}>
                    {m.settings_suggested_question_label({ position })}
                  </label>
                  <div
                    className="min-w-0 flex-1"
                    ref={row.id === focusRowKey ? focusNewRow : undefined}
                  >
                    <TextField
                      error={errors?.[index]}
                      id={id}
                      onChange={(event) => {
                        onChange(
                          questions.map((item, itemIndex) =>
                            itemIndex === index ? event.target.value : item,
                          ),
                        );
                      }}
                      value={row.question}
                    />
                  </div>
                  <div className="flex min-h-control flex-none items-center gap-0.5">
                    <Button
                      aria-label={m.settings_suggested_question_move_up({
                        position,
                      })}
                      disabled={index === 0}
                      icon={<Icon name="chevronUp" size={14} />}
                      onClick={() => {
                        move(row.id, index - 1);
                      }}
                      size="sm"
                      variant="quiet"
                    />
                    <Button
                      aria-label={m.settings_suggested_question_move_down({
                        position,
                      })}
                      disabled={index === rows.length - 1}
                      icon={<Icon name="chevronDown" size={14} />}
                      onClick={() => {
                        move(row.id, index + 1);
                      }}
                      size="sm"
                      variant="quiet"
                    />
                    <Button
                      aria-label={m.settings_suggested_question_remove({
                        position,
                      })}
                      icon={<Icon name="close" size={14} />}
                      onClick={() => {
                        remove(index);
                      }}
                      size="sm"
                      variant="quiet"
                    />
                  </div>
                </div>
              );
            }}
          />
        </div>
      )}
      <div className="mt-3 flex items-center gap-2.5">
        <Button disabled={full} onClick={add} size="sm" variant="secondary">
          {m.settings_suggested_questions_add()}
        </Button>
        {full && (
          <span className="text-micro text-[var(--content-fg-subtle)]">
            {m.settings_suggested_questions_limit()}
          </span>
        )}
      </div>
    </div>
  );
}
